/**
 * SalesLeadDetail.tsx — Sales / Inbound Leads per-lead detail (§8.F).
 *
 * Two-level pattern exactly like the deal detail: reached from the queue via
 * selectedLeadId (mirroring selectedDealId, Dashboard.tsx), Back returns to
 * the queue. The lead is fetched FRESH by id; status writes land in a keyed
 * override (the SalesDealDetail pattern — no fetched-state copied into
 * local state).
 *
 * VOCABULARY (§8.A, held): this page shows an INBOUND LEAD — a pre-pipeline
 * sales_contacts row. Converting it creates a deal that ENTERS the pipeline
 * at the New Lead stage; the two never alias in copy.
 *
 * Actions: Convert to deal (the load-bearing one — ConvertLeadModal, then
 * the caller navigates to the born deal), Mark contacted / Disqualify
 * (§8.E status writes with a two-step inline confirm; either exits the
 * L8-2 queue). The source-email provenance panel is DELIBERATELY absent in
 * v1: no by-id inbound-email read path exists, and this page renders only
 * fields the read path exposes (data honesty) — it ships with L3.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import {
  ArrowLeft, Mail, Phone, Building2, Briefcase, MapPin, Linkedin, User, Inbox,
} from 'lucide-react'
import { Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesLead } from '../../types'
import type { SalesDeal as WireDeal } from '../../../../api/types'
import { useAsync, fetchLead, updateLeadStatus } from '../../api'
import { ConvertLeadModal } from './ConvertLeadModal'
import { SalesAuditStrip } from './SalesAuditStrip'
import { EditLeadDrawer } from './EditLeadDrawer'
import { sourceLabel } from './vocab'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { statusContact } from '../../../../theme/tokens'

const STATUS_CONFIG: Record<SalesLead['status'], { label: string; bg: string; text: string }> = {
  new:         { label: 'New',         ...statusContact.new },
  contacted:   { label: 'Contacted',   ...statusContact.contacted },
  qualified:   { label: 'Qualified',   ...statusContact.qualified },
  unqualified: { label: 'Unqualified', ...statusContact.unqualified },
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-edge-row last:border-0">
      <div className="w-8 h-8 rounded-control bg-surface-input flex items-center justify-center text-ink-faint flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-11 text-ink-muted font-medium mb-1">{label}</div>
        <div className="text-13 text-ink-primary font-medium truncate">{value || '—'}</div>
      </div>
    </div>
  )
}

/** Two-step inline confirm (§8.F "status writes with confirm"): first click
 *  arms, second confirms, anywhere-else cancels via the Cancel button. */
function ConfirmableAction({ label, confirmLabel, busy, onConfirm }: {
  label: string
  confirmLabel: string
  busy: boolean
  onConfirm: () => void
}) {
  const [armed, setArmed] = useState(false)
  if (!armed) {
    return <button className="k-btn-secondary" onClick={() => setArmed(true)}>{label}</button>
  }
  return (
    <span className="inline-flex items-center gap-2">
      <button className="k-btn-secondary" onClick={() => setArmed(false)}>Cancel</button>
      <button className="k-btn" onClick={() => { if (!busy) onConfirm() }}>{busy ? 'Saving…' : confirmLabel}</button>
    </span>
  )
}

export function SalesLeadDetail({ leadId, onBack, onConverted, onOpenDeal }: {
  leadId: string
  onBack: () => void
  /** The born deal (wire) — the caller navigates to it via selectedDealId. */
  onConverted: (wire: WireDeal) => void
  /** Open an existing deal (the already-converted defense path). */
  onOpenDeal: (id: string) => void
}) {
  const leadQ = useAsync(() => fetchLead(leadId), [leadId])
  // Status-write responses override the fetched lead (keyed by id).
  const [override, setOverride] = useState<{ id: string; lead: SalesLead } | null>(null)
  const [converting, setConverting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [writing, setWriting] = useState(false)

  const lead = override && override.id === leadId ? override.lead : leadQ.data

  if (leadQ.loading) return <LoadingState />
  if (leadQ.error || !lead) return <ErrorState message={leadQ.error ?? 'Lead not found'} />

  const cfg = STATUS_CONFIG[lead.status]
  const converted = lead.convertedDealId !== ''

  async function setStatus(status: 'contacted' | 'unqualified') {
    setWriting(true)
    try {
      const next = await updateLeadStatus(leadId, status)
      setOverride({ id: leadId, lead: next })
      toast.success(status === 'contacted'
        ? 'Lead marked contacted — it has left the Inbound Leads queue'
        : 'Lead disqualified — it has left the Inbound Leads queue')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update lead'))
    } finally {
      setWriting(false)
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      {/* Back link */}
      <button onClick={onBack}
        className="flex items-center gap-2 text-12 font-medium text-ink-secondary hover:text-ink-primary transition-colors self-start">
        <ArrowLeft size={14} /> Back to inbound leads
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Avatar name={lead.name} size={40} />
          <div>
            <h1 className="text-section-title font-semibold text-ink-primary leading-tight">{lead.name}</h1>
            <p className="text-13 text-ink-secondary mt-1">
              {[lead.title, lead.company].filter(Boolean).join(' · ') || 'Inbound lead'}
            </p>
          </div>
          <span className="k-badge ml-2" style={{ background: cfg.bg, color: cfg.text }}>
            <span className="k-badge-dot" />{cfg.label.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* SB-2: edit is legal in every status — converted included (the
              contact record outlives the queue). */}
          <button className="k-btn-secondary" onClick={() => setEditing(true)}>Edit</button>
          {converted ? (
            <button className="k-btn" onClick={() => onOpenDeal(lead.convertedDealId)}>View deal</button>
          ) : (
            <>
              <ConfirmableAction label="Mark contacted" confirmLabel="Confirm contacted"
                busy={writing} onConfirm={() => setStatus('contacted')} />
              <ConfirmableAction label="Disqualify" confirmLabel="Confirm disqualify"
                busy={writing} onConfirm={() => setStatus('unqualified')} />
              <button className="k-btn k-btn--lg" onClick={() => setConverting(true)}>Convert to deal</button>
            </>
          )}
        </div>
      </div>

      {converted && (
        <div className="text-12 text-ink-secondary">
          This lead was converted — it lives in the pipeline as a deal now.
        </div>
      )}

      {/* Two-column body: contact info + change history | provenance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* The wide cell is a COLUMN of cards, not one card: the audit strip
            sits under Contact info at the same width, which is the Account
            Detail arrangement. Each cell here used to BE a single k-card. */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Contact info</div>
            </div>
            <div className="px-5 py-2">
              <InfoRow icon={<Mail size={14} />}      label="Email"    value={lead.email} />
              <InfoRow icon={<Phone size={14} />}     label="Phone"    value={lead.phone} />
              <InfoRow icon={<Building2 size={14} />} label="Company"  value={lead.company} />
              <InfoRow icon={<Briefcase size={14} />} label="Title"    value={lead.title} />
              <InfoRow icon={<MapPin size={14} />}    label="Location" value={lead.location} />
              <InfoRow icon={<Linkedin size={14} />}  label="LinkedIn" value={lead.linkedinUrl ?? ''} />
            </div>
          </div>

          {/* Slice B (0117): the audit strip. An inbound lead IS a
              sales_contacts row (P1-2 kept the contact-as-lead model), so its
              history is the CONTACT's history — the §8.A vocabulary split is
              about language, not about which record this is. Full card
              variant: this page's cells are k-cards, unlike the drawer. */}
          <SalesAuditStrip recordType="contacts" recordId={leadId} />
        </div>

        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Lead</div>
          </div>
          <div className="px-5 py-2">
            <InfoRow icon={<Inbox size={14} />} label="Source"   value={sourceLabel(lead.source)} />
            <InfoRow icon={<User size={14} />}  label="Assigned" value={lead.assignedTo} />
            <InfoRow icon={<Briefcase size={14} />} label="Received" value={lead.createdLabel} />
          </div>
          {lead.notes !== '' && (
            <div className="px-5 pb-4">
              <div className="k-eyebrow uppercase mb-1">Notes</div>
              <div className="text-13 text-ink-body whitespace-pre-wrap">{lead.notes}</div>
            </div>
          )}
        </div>
      </div>

      {converting && (
        <ConvertLeadModal
          lead={lead}
          onCancel={() => setConverting(false)}
          onConverted={onConverted}
        />
      )}

      {editing && (
        <EditLeadDrawer
          lead={lead}
          onClose={() => setEditing(false)}
          onSaved={next => setOverride({ id: leadId, lead: next })}
        />
      )}
    </div>
  )
}
